import { motion } from "framer-motion";
import { type FC, useEffect, useRef } from "react";
import { colors } from "widgets/utils";

export const ErrorPage: FC<{ message: string }> = ({ message }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      style={{
        width: "100%",
        height: "100%",
        background: "white",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        margin: "auto",
        maxWidth: 640,
      }}
      data-testid="loading-dna"
    >
      <Animation />
      <div
        style={{
          textAlign: "left",
          fontSize: 40,
          fontWeight: 350,
          lineHeight: 0.75,
          margin: 10,
        }}
      >
        Something went wrong
      </div>
      <pre id="error-message" style={{ color: "var(--primary-main)" }} data-testid="error-message">
        {message}
      </pre>
    </motion.div>
  );
};

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
}

const Animation = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    let frame = 0;
    let animationFrameId: number;

    // Create particles for broken DNA pieces
    let particles: Particle[] = [];
    const addParticle = (x: number, y: number, vx: number, vy: number) => {
      particles.push({ x, y, vx, vy, life: 100 });
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update and draw particles
      particles = particles.filter((p) => p.life > 0);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.1; // gravity
        p.life -= 1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 50, 50, ${p.life / 100})`;
        ctx.fill();
      });

      // Draw the deteriorating DNA strands
      for (let i = 0; i < canvas.width; i += 4) {
        const baseY = canvas.height / 2;
        const amplitude = 30;
        const frequency = 0.02;
        const speed = 0.05;

        // Create multiple breakdown zones
        const breakdown = (pos: number) => {
          return Math.sin(frame * 0.1 + pos * 0.3) * Math.sin(frame * 0.05) * Math.cos(pos * 0.1);
        };

        // Calculate positions with increasing chaos
        const chaos = breakdown(i) * 20;
        const y1 = baseY + Math.sin(i * frequency + frame * speed) * amplitude + chaos;
        const y2 = baseY + Math.sin((i + 100) * frequency + frame * speed) * amplitude - chaos;

        // Randomly break strands and create particles
        if (Math.random() > 0.995) {
          addParticle(i, y1, (Math.random() - 0.5) * 3, -Math.random() * 5);
          continue;
        }

        // Calculate error intensity based on position and time
        const errorIntensity = Math.abs(Math.sin(frame * 0.02 + i * 0.1));
        const isError = errorIntensity > 0.7;

        // Draw main strands with glitch effect
        if (Math.random() > 0.1) {
          // Occasionally skip drawing to create gaps
          ctx.beginPath();
          ctx.strokeStyle = isError ? colors.dangerLight : colors.primaryMain;
          ctx.lineWidth = isError ? 3 : 2;
          ctx.moveTo(i, y1);
          ctx.lineTo(i + 4, y1);
          ctx.stroke();

          ctx.beginPath();
          ctx.strokeStyle = isError ? colors.dangerMain : colors.successMain;
          ctx.moveTo(i, y2);
          ctx.lineTo(i + 4, y2);
          ctx.stroke();
        }

        // Draw connecting lines with deterioration
        if (i % 20 === 0 && i > 0 && Math.random() > 0.3) {
          ctx.beginPath();
          ctx.strokeStyle = isError
            ? `rgba(255, ${Math.random() * 100}, ${Math.random() * 100}, ${0.5 + Math.random() * 0.5})`
            : `rgba(142, 142, 144, ${0.5 + Math.random() * 0.5})`;
          ctx.lineWidth = isError ? Math.random() * 2 + 1 : 1;

          // Draw breaking connections
          const midPoint = (y1 + y2) / 2;
          const offset = (Math.random() - 0.5) * 20;

          if (Math.random() > 0.5) {
            ctx.moveTo(i, y1);
            ctx.lineTo(i + offset, midPoint);
            ctx.moveTo(i - offset, midPoint);
            ctx.lineTo(i, y2);
          } else {
            ctx.moveTo(i, y1);
            ctx.lineTo(i, y2);
          }
          ctx.stroke();
        }
      }

      // Draw glitchy "500" text
      ctx.font = "bold 48px monospace";
      const text = "!";
      const textWidth = ctx.measureText(text).width;
      const textX = canvas.width / 2 - textWidth / 2;
      const textY = canvas.height / 2 + 8;

      // Main text
      ctx.fillStyle = "rgba(255, 50, 50, 0.8)";
      ctx.fillText(text, textX, textY);

      // Glitch effect on text
      if (Math.random() > 0.8) {
        const glitchOffset = Math.random() * 10 - 5;
        ctx.fillStyle = "rgba(50, 255, 255, 0.5)";
        ctx.fillText(text, textX + glitchOffset, textY);
        ctx.fillStyle = "rgba(255, 50, 255, 0.5)";
        ctx.fillText(text, textX - glitchOffset, textY);
      }

      frame = frame + 1;
      animationFrameId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return <canvas ref={canvasRef} width={400} height={200} />;
};
